import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import { loadOrder } from '../redux/actions';
/**
 * The `useOrder` hook returns the current loaded order.
 *
 * This function has a side effect that will automatically start loading the
 * subscription, if needed, upon initialization.
 *
 * @example
 *
 * ```javascript
 * import { useOrder } from '@chordcommerce/react-autonomy'
 *
 * export default () => {
 *   const { order, isFetching } = useOrder({ orderNumber: PLANT-10000 })
 *
 *   if (isFetching) {
 *     return (
 *       <p>Loading…</p>
 *     )
 *   }
 *
 *   return (
 *     <p>Current state of Order # {order.orderNumber} is {order.state}</p>
 *   )
 * }
 * ```
 *
 * @category Hook
 * @param {Object} props
 * @param {number | string} props.orderNumber - The order number of the order to load.
 * @returns {UseOrder}
 */
export function useOrder({ orderNumber }) {
    const ordersState = useSelector((state) => state.orders, shallowEqual);
    const isFetching = ordersState.loading.includes(orderNumber);
    const isLoaded = ordersState.success.includes(orderNumber);
    const error = ordersState.error?.[orderNumber];
    const dispatch = useDispatch();
    const actionDispatches = useMemo(() => bindActionCreators({
        loadOrder: loadOrder(orderNumber),
    }, dispatch), [dispatch, orderNumber]);
    useEffect(() => {
        if (!orderNumber || isFetching || isLoaded || error)
            return;
        if (Object.keys(ordersState.data).length === 0) {
            actionDispatches.loadOrder();
        }
    }, [ordersState, orderNumber, actionDispatches.loadOrder]);
    return {
        order: ordersState.data?.[orderNumber],
        error,
        isLoaded,
        isFetching,
        ...actionDispatches,
    };
}
